import { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Mail, Shield, AlertTriangle, CheckCircle2, Clock, RefreshCw, Copy, Search, Wifi, WifiOff, Activity } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import api from '@/lib/api';
import { toast } from 'sonner';

export default function EmailOpsPage() {
  const [overview, setOverview] = useState(null);
  const [domains, setDomains] = useState([]);
  const [realtime, setRealtime] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('domains');
  const [dnsChecking, setDnsChecking] = useState(false);
  const refreshRef = useRef(null);

  useEffect(() => { loadData(); return () => { if (refreshRef.current) clearInterval(refreshRef.current); }; }, []);

  // Auto-refresh every 30s for verification loop
  useEffect(() => {
    if (activeTab === 'dns' || activeTab === 'realtime') {
      refreshRef.current = setInterval(loadData, 30000);
      return () => clearInterval(refreshRef.current);
    }
  }, [activeTab]);

  const loadData = async () => {
    try {
      const [ovRes, domRes, rtRes] = await Promise.all([
        api.get('/email-ops/overview'),
        api.get('/email-ops/domains'),
        api.get('/email-ops/realtime').catch(() => ({ data: null })),
      ]);
      setOverview(ovRes.data);
      setDomains(domRes.data);
      setRealtime(rtRes.data);
    } catch(e) {}
    setLoading(false);
  };

  const sc = (s) => ({
    active: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
    verified: 'bg-blue-500/15 text-blue-400 border-blue-500/30',
    verifying: 'bg-blue-500/10 text-blue-300 border-blue-300/20',
    pending_dns: 'bg-amber-500/15 text-amber-400 border-amber-500/30',
    danger: 'bg-red-500/15 text-red-400 border-red-500/30',
    warmup_active: 'bg-cyan-500/15 text-cyan-400 border-cyan-500/30',
    paused: 'bg-red-500/10 text-red-300 border-red-300/20',
  }[s] || 'bg-secondary/50 text-muted-foreground border-border/50');

  const hc = (v) => v >= 80 ? 'text-emerald-400' : v >= 50 ? 'text-amber-400' : 'text-red-400';

  return (
    <div className="space-y-5">
      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
        <h1 className="text-2xl font-semibold tracking-tight font-['Space_Grotesk']">Email Operations</h1>
        <p className="text-sm text-muted-foreground">Infrastructure orchestration, DNS management, visibility dashboard</p>
      </motion.div>

      {/* Stats */}
      {overview && (
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2.5">
          {[
            { l: 'Domains', v: overview.total_domains },
            { l: 'Pending DNS', v: overview.pending_dns },
            { l: 'Verified', v: overview.verified },
            { l: 'Warmup', v: overview.warmup_active },
            { l: 'Active', v: overview.active },
            { l: 'Danger', v: overview.dangerous },
            { l: 'Kapasite/gün', v: overview.daily_safe_capacity },
          ].map((s, i) => (
            <Card key={i} className="border-border/70"><CardContent className="p-3 text-center"><p className="text-xl font-bold">{s.v}</p><p className="text-[9px] text-muted-foreground mt-0.5">{s.l}</p></CardContent></Card>
          ))}
        </div>
      )}

      {/* ANG Status */}
      {overview && (
        <div className={`flex items-center gap-3 px-3 py-2 rounded-lg border text-xs ${overview.ang_mode === 'live' && overview.ang_connected ? 'bg-emerald-500/5 border-emerald-500/20' : 'bg-amber-500/5 border-amber-500/20'}`}>
          {overview.ang_connected ? <Wifi className="w-3.5 h-3.5 text-emerald-400" /> : <WifiOff className="w-3.5 h-3.5 text-amber-400" />}
          <span className="font-medium">{overview.ang_mode === 'live' && overview.ang_connected ? 'ANG Digital Live (Signed HMAC)' : 'Mock Mode'}</span>
          <span className={`ml-auto px-2 py-0.5 rounded text-[9px] font-bold ${overview.ang_mode === 'live' ? 'bg-emerald-500/15 text-emerald-400' : 'bg-amber-500/15 text-amber-400'}`}>{overview.ang_mode.toUpperCase()}</span>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-0.5 border-b border-border/50">
        {[['domains','Domainler'],['dns','DNS Wizard'],['warmup','Warmup'],['health','Health'],['realtime','Realtime']].map(([k,l]) => (
          <button key={k} onClick={() => setActiveTab(k)} className={`px-4 py-2 text-xs font-medium border-b-2 transition-colors ${activeTab === k ? 'border-[hsl(var(--primary))] text-[hsl(var(--primary))]' : 'border-transparent text-muted-foreground hover:text-foreground'}`}>{l}</button>
        ))}
      </div>

      {/* DOMAINS TAB */}
      {activeTab === 'domains' && (
        <Card className="border-border/70">
          <CardHeader className="flex flex-row items-center justify-between py-3">
            <CardTitle className="text-sm">Email Domainleri ({domains.length})</CardTitle>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" className="text-[10px] h-7 gap-1"
                onClick={async () => {
                  const ids = domains.filter(d => d.status === 'not_created').map(d => d.site_id);
                  if (!ids.length) { toast.info('Hepsi olusturulmus'); return; }
                  try { const { data } = await api.post('/email-infra/bulk-create', { site_ids: ids, dry_run: false }); toast.success(`${data.created} domain kaydedildi`); loadData(); } catch(e) { toast.error('Basarisiz'); }
                }}><Mail className="w-3 h-3" /> Tumunu Kaydet ({domains.filter(d => d.status === 'not_created').length})</Button>
              <Button size="sm" variant="ghost" className="text-[10px] h-7" onClick={loadData}><RefreshCw className="w-3 h-3" /></Button>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead><tr className="border-b border-border/30 text-[10px] text-muted-foreground uppercase tracking-wider">
                  <th className="text-left p-2.5">Site</th><th className="text-left p-2.5">Domain</th><th className="p-2.5">Status</th><th className="p-2.5">Verify</th><th className="p-2.5">Warmup</th><th className="p-2.5">Limit</th><th className="p-2.5">Health</th>
                </tr></thead>
                <tbody>
                  {domains.map((d, i) => (
                    <tr key={i} className="border-b border-border/10 hover:bg-secondary/20 transition-colors">
                      <td className="p-2.5 font-medium">{d.brand}</td>
                      <td className="p-2.5 font-mono text-[10px]">{d.domain}</td>
                      <td className="p-2.5 text-center"><span className={`px-1.5 py-0.5 rounded text-[8px] font-bold border ${sc(d.status)}`}>{d.status}</span></td>
                      <td className="p-2.5 text-center text-[10px] text-muted-foreground">{d.verification}</td>
                      <td className="p-2.5 text-center text-[10px]">{d.warmup_day > 0 ? `D${d.warmup_day}` : '-'}</td>
                      <td className="p-2.5 text-center font-mono">{d.daily_limit}</td>
                      <td className={`p-2.5 text-center font-bold ${hc(d.health_score)}`}>{d.health_score || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* DNS WIZARD TAB */}
      {activeTab === 'dns' && (
        <Card className="border-border/70">
          <CardHeader className="flex flex-row items-center justify-between py-3">
            <CardTitle className="text-sm">DNS Activation Wizard</CardTitle>
            <div className="flex gap-2">
              <Button size="sm" className="bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] text-[10px] h-7 gap-1" disabled={dnsChecking}
                onClick={async () => {
                  setDnsChecking(true);
                  try { const { data } = await api.post('/dns/bulk-validate'); toast.success(`${data.checked} domain kontrol edildi`); loadData(); } catch(e) { toast.error('Kontrol basarisiz'); }
                  setDnsChecking(false);
                }}>{dnsChecking ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Search className="w-3 h-3" />} Tum DNS Kontrol Et + Auto Verify</Button>
              <span className="text-[9px] text-muted-foreground self-center">30sn auto-refresh</span>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {domains.filter(d => d.status !== 'not_created').map((d, i) => (
                <div key={i} className={`flex items-center gap-3 p-3 rounded-lg border ${d.status === 'verified' || d.status === 'active' ? 'bg-emerald-500/5 border-emerald-500/15' : d.status === 'verifying' ? 'bg-blue-500/5 border-blue-500/15' : 'bg-amber-500/5 border-amber-500/15'}`}>
                  <div className="flex-1">
                    <p className="text-xs font-medium">{d.domain}</p>
                    <div className="flex gap-3 mt-1">
                      {['SPF','DKIM','DMARC','Mail','Click'].map(r => (
                        <span key={r} className="text-[9px] text-muted-foreground">{d.status === 'verified' || d.status === 'active' ? '✅' : d.status === 'verifying' ? '🔄' : '⏳'} {r}</span>
                      ))}
                    </div>
                  </div>
                  <span className={`px-2 py-0.5 rounded text-[9px] font-bold border ${sc(d.status)}`}>{d.status}</span>
                </div>
              ))}
              {domains.filter(d => d.status === 'not_created').length > 0 && (
                <p className="text-[10px] text-muted-foreground text-center py-3">{domains.filter(d => d.status === 'not_created').length} domain henuz email infra olusturulmamis</p>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* WARMUP TAB */}
      {activeTab === 'warmup' && (
        <Card className="border-border/70">
          <CardHeader><CardTitle className="text-sm">Warmup Progress</CardTitle></CardHeader>
          <CardContent>
            {domains.filter(d => d.status !== 'not_created').length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">Warmup verisi yok</p>
            ) : (
              <div className="space-y-2.5">
                {domains.filter(d => d.status !== 'not_created').map((d, i) => (
                  <div key={i} className="flex items-center gap-4 p-3 rounded-lg bg-secondary/20 border border-border/30">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1.5">
                        <p className="text-xs font-medium">{d.domain}</p>
                        <span className={`px-1.5 py-0.5 rounded text-[8px] font-bold border ${sc(d.status)}`}>{d.status}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-1.5 bg-border/30 rounded-full overflow-hidden">
                          <div className="h-full bg-cyan-400 rounded-full transition-all" style={{ width: `${Math.min(d.warmup_day * 3.3, 100)}%` }} />
                        </div>
                        <span className="text-[9px] text-muted-foreground whitespace-nowrap">Gun {d.warmup_day}/30</span>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-bold">{d.daily_limit}</p>
                      <p className="text-[8px] text-muted-foreground">limit/gun</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-bold text-muted-foreground">{d.sent_today}</p>
                      <p className="text-[8px] text-muted-foreground">gonderildi</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* HEALTH TAB */}
      {activeTab === 'health' && (
        <Card className="border-border/70">
          <CardHeader><CardTitle className="text-sm">Domain Reputation & Deliverability</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-2">
              {domains.filter(d => d.status !== 'not_created').map((d, i) => (
                <div key={i} className={`flex items-center gap-4 p-3 rounded-lg border ${d.health_score > 0 && d.health_score < 50 ? 'bg-red-500/5 border-red-500/15' : 'bg-secondary/20 border-border/30'}`}>
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold border ${d.health_score >= 80 ? 'border-emerald-500/30 text-emerald-400' : d.health_score >= 50 ? 'border-amber-500/30 text-amber-400' : 'border-red-500/30 text-red-400'}`}>
                    {d.health_score || '—'}
                  </div>
                  <div className="flex-1">
                    <p className="text-xs font-medium">{d.domain}</p>
                    <p className="text-[9px] text-muted-foreground">Sent: {d.sent_today} | Limit: {d.daily_limit} | Day: {d.warmup_day}</p>
                  </div>
                  <span className={`px-2 py-0.5 rounded text-[8px] font-bold border ${sc(d.status)}`}>{d.status}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* REALTIME TAB */}
      {activeTab === 'realtime' && (
        <div className="space-y-4">
          <Card className="border-border/70">
            <CardHeader className="flex flex-row items-center justify-between py-3">
              <CardTitle className="text-sm flex items-center gap-2"><Activity className="w-4 h-4 text-emerald-400" /> Realtime Operations</CardTitle>
              <span className="text-[9px] text-emerald-400 flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> 30s auto-refresh</span>
            </CardHeader>
            <CardContent>
              {realtime ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <div className="p-3 rounded-lg bg-secondary/30 border border-border/50 text-center"><p className="text-lg font-bold">{realtime.total_sent_today}</p><p className="text-[9px] text-muted-foreground">Bugun Gonderilen</p></div>
                    <div className="p-3 rounded-lg bg-secondary/30 border border-border/50 text-center"><p className="text-lg font-bold">{realtime.remaining_today}</p><p className="text-[9px] text-muted-foreground">Kalan Kapasite</p></div>
                    <div className="p-3 rounded-lg bg-secondary/30 border border-border/50 text-center"><p className="text-lg font-bold">{realtime.active_domains}</p><p className="text-[9px] text-muted-foreground">Aktif Domain</p></div>
                    <div className="p-3 rounded-lg bg-secondary/30 border border-border/50 text-center"><p className="text-lg font-bold">{realtime.warmup_domains}</p><p className="text-[9px] text-muted-foreground">Warmup</p></div>
                  </div>
                  {realtime.alerts && realtime.alerts.length > 0 && (
                    <div>
                      <h4 className="text-xs font-semibold mb-2 flex items-center gap-1.5"><AlertTriangle className="w-3.5 h-3.5 text-amber-400" /> Alerts</h4>
                      {realtime.alerts.map((a, i) => (
                        <div key={i} className={`flex items-center gap-2 p-2.5 rounded-lg mb-1.5 border ${a.type === 'danger' || a.type === 'spam' ? 'bg-red-500/5 border-red-500/15' : 'bg-amber-500/5 border-amber-500/15'}`}>
                          <span className="text-[10px]">{a.type === 'danger' ? '🔴' : a.type === 'spam' ? '⚠️' : '🟡'}</span>
                          <span className="text-xs">{a.message}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {realtime.paused_domains && realtime.paused_domains.length > 0 && (
                    <div><h4 className="text-xs font-semibold mb-1 text-red-400">Paused Domains</h4>
                      {realtime.paused_domains.map((d, i) => <span key={i} className="inline-block px-2 py-0.5 bg-red-500/10 text-red-400 rounded text-[10px] mr-1 mb-1">{d}</span>)}
                    </div>
                  )}
                  {(!realtime.alerts || realtime.alerts.length === 0) && (!realtime.paused_domains || realtime.paused_domains.length === 0) && (
                    <div className="text-center py-4"><CheckCircle2 className="w-6 h-6 text-emerald-400 mx-auto mb-2" /><p className="text-xs text-muted-foreground">Tum sistemler normal calisiyor</p></div>
                  )}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground text-center py-8">Realtime veri yukleniyor...</p>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
